import { fetchWithSupabaseAuth } from "./supabaseClient.js";

function resolveErrorMessage(payload, fallbackMessage) {
  if (payload && typeof payload.detail === "string" && payload.detail.trim()) {
    return payload.detail.trim();
  }

  if (payload && typeof payload.message === "string" && payload.message.trim()) {
    return payload.message.trim();
  }

  return fallbackMessage;
}

export function formatPasswordResetEmailError(error) {
  const message = error instanceof Error ? error.message.trim() : "";
  if (!message) {
    return "Impossible d’envoyer le lien de réinitialisation.";
  }

  if (message.includes("APP_PUBLIC_URL ne peut pas pointer vers localhost")) {
    return "APP_PUBLIC_URL ne doit pas pointer vers localhost. Utilisez l’URL publique en ligne de l’application.";
  }

  if (message.includes("APP_PUBLIC_URL")) {
    return "Le serveur n’est pas configuré pour envoyer des liens vers l’application en ligne. Ajoutez APP_PUBLIC_URL (ex. https://www.trans-ia.ca) dans votre fichier .env local ou sur Railway.";
  }

  if (message.includes("redirect_to est requis")) {
    return "Le serveur ne peut pas générer un lien de réinitialisation. Vérifiez la configuration APP_PUBLIC_URL côté backend.";
  }

  return message;
}

async function parseJsonResponse(response, fallbackMessage) {
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(resolveErrorMessage(payload, fallbackMessage));
  }

  return payload;
}

async function requestJson(url, init, fallbackMessage) {
  const response = await fetchWithSupabaseAuth(url, init);
  return parseJsonResponse(response, fallbackMessage);
}

export async function fetchAccessUsers() {
  return requestJson("/api/access/users", undefined, "Impossible de charger les utilisateurs.");
}

export async function createAccessUser(payload) {
  return requestJson("/api/access/users", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, "Impossible de créer le compte utilisateur.");
}

export async function updateAccessUserPassword(userId, payload) {
  return requestJson(`/api/access/users/${encodeURIComponent(userId)}/password`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, "Impossible de mettre à jour le mot de passe utilisateur.");
}

export async function sendAccessUserPasswordResetEmail(userId) {
  const resolvedUserId = String(userId || "").trim();
  if (!resolvedUserId) {
    throw new Error("Aucun identifiant utilisateur n’est disponible pour envoyer le lien de réinitialisation.");
  }

  return requestJson(
    `/api/access/users/${encodeURIComponent(resolvedUserId)}/password-reset-email`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    },
    "Impossible d’envoyer le lien de réinitialisation.",
  );
}

export async function fetchAccessRoles() {
  return requestJson("/api/access/roles", undefined, "Impossible de charger les rôles.");
}

export async function fetchAccessPermissionCatalog() {
  return requestJson("/api/access/permissions", undefined, "Impossible de charger le catalogue de permissions.");
}

export async function fetchAccessAuditEvents() {
  return requestJson("/api/access/audit", undefined, "Impossible de charger l’audit d’accès.");
}

export async function updateAccessUserRole(userId, payload) {
  return requestJson(`/api/access/users/${encodeURIComponent(userId)}/role`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, "Impossible de mettre à jour le rôle utilisateur.");
}

export async function assignAccessUserRole(payload) {
  return requestJson("/api/access/users/role", {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, "Impossible d’attribuer le rôle utilisateur.");
}

export async function removeAccessUserRole(userId) {
  return requestJson(`/api/access/users/${encodeURIComponent(userId)}/role`, {
    method: "DELETE",
  }, "Impossible de retirer l’affectation RBAC utilisateur.");
}

export async function suspendAccessUser(userId, { confirmSharedIdentity = false } = {}) {
  return requestJson(`/api/access/users/${encodeURIComponent(userId)}/suspend`, {
    method: "POST",
    headers: confirmSharedIdentity ? { "X-Grain-Confirm-Shared-Identity": "true" } : undefined,
  }, "Impossible de suspendre complètement le compte utilisateur.");
}

export async function reactivateAccessUser(userId, payload) {
  return requestJson(`/api/access/users/${encodeURIComponent(userId)}/reactivate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, "Impossible de réactiver le compte utilisateur.");
}

export async function deleteAccessUser(userId, { confirmSharedIdentity = false } = {}) {
  return requestJson(`/api/access/users/${encodeURIComponent(userId)}`, {
    method: "DELETE",
    headers: confirmSharedIdentity ? { "X-Grain-Confirm-Shared-Identity": "true" } : undefined,
  }, "Impossible de supprimer définitivement le compte utilisateur.");
}

export async function createAccessRole(payload) {
  return requestJson("/api/access/roles", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, "Impossible de créer le rôle custom.");
}

export async function updateAccessRole(roleKey, payload) {
  return requestJson(`/api/access/roles/${encodeURIComponent(roleKey)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, "Impossible de mettre à jour le rôle.");
}

export async function replaceAccessRolePermissions(roleKey, payload) {
  return requestJson(`/api/access/roles/${encodeURIComponent(roleKey)}/permissions`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }, "Impossible d’enregistrer la matrice du rôle.");
}

export async function archiveAccessRole(roleKey) {
  return requestJson(`/api/access/roles/${encodeURIComponent(roleKey)}/archive`, {
    method: "POST",
  }, "Impossible d’archiver le rôle.");
}
